import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Check, RotateCw, X } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { InstanceDetail, ProvisioningStep } from "@/types/api";

/** Order and labels match PROVISIONING_STEPS in the control plane. */
const STEPS: Array<{ key: string; label: string }> = [
  { key: "supabase_user", label: "Admin user" },
  { key: "credentials_email", label: "Credentials email" },
  { key: "namespace", label: "Namespace" },
  { key: "secrets", label: "Secrets" },
  { key: "db_record", label: "Control-plane record" },
  { key: "helm_dispatch", label: "Helm install triggered" },
];

function StepRow({ label, step }: { label: string; step?: ProvisioningStep }) {
  const done = step?.status === "done";
  const failed = step?.status === "failed";
  return (
    <li className="flex items-start gap-3 py-1.5">
      <span aria-hidden="true" className="mt-0.5">
        {done ? (
          <Check className="h-4 w-4 text-emerald-600" />
        ) : failed ? (
          <X className="h-4 w-4 text-destructive" />
        ) : (
          <span className="block h-4 w-4 rounded-full border border-muted-foreground/40" />
        )}
      </span>
      <span className="flex-1">
        <span className="text-sm">{label}</span>
        {step?.skipped ? (
          <span className="block text-xs text-muted-foreground">{step.skipped}</span>
        ) : null}
        {failed && step?.error ? (
          <span className="block text-xs text-destructive">{step.error}</span>
        ) : null}
      </span>
      <span className="text-xs text-muted-foreground">
        {done ? "done" : failed ? "failed" : "not run"}
      </span>
    </li>
  );
}

/**
 * Provisioning is a sequence of steps that can stop partway. Before this the
 * API answered 201 no matter what, so an instance with a namespace, secrets
 * and no workload at all looked identical to a healthy one.
 */
export function ProvisioningStepsCard({
  instance,
}: {
  instance: InstanceDetail["instance"];
}) {
  const qc = useQueryClient();
  const steps = instance.provisioningSteps;

  const retry = useMutation({
    mutationFn: () =>
      api.post<{ message?: string; failedStep?: string }>(
        `/api/admin/instances/${instance.name}/retry-provisioning`
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["instance", instance.name] });
      qc.invalidateQueries({ queryKey: ["instances"] });
      toast.success(res.message ?? "Provisioning retried");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Retry failed"),
  });

  // Nothing recorded and the instance is long past provisioning: this row
  // predates step tracking, so an empty checklist would just be misleading.
  if (!steps && instance.status !== "provisioning" && instance.status !== "failed") {
    return null;
  }

  const incomplete = STEPS.some((s) => steps?.[s.key]?.status !== "done");
  const failedStep = STEPS.find((s) => steps?.[s.key]?.status === "failed");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Provisioning
          {failedStep ? (
            <Badge variant="destructive">Stopped at {failedStep.label}</Badge>
          ) : incomplete ? (
            <Badge variant="secondary">Incomplete</Badge>
          ) : (
            <Badge variant="secondary">Complete</Badge>
          )}
        </CardTitle>
        <CardDescription>
          Every step is safe to repeat. Retry replays only what has not
          finished — nothing is torn down.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="divide-y">
          {STEPS.map((s) => (
            <StepRow key={s.key} label={s.label} step={steps?.[s.key]} />
          ))}
        </ul>

        {instance.helmInstall ? (
          <p className="text-xs text-muted-foreground">
            Helm install{" "}
            {instance.helmInstall.success ? "succeeded" : "failed"}
            {instance.helmInstall.runUrl ? (
              <>
                {" — "}
                <a
                  className="underline"
                  href={instance.helmInstall.runUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  workflow run
                </a>
              </>
            ) : null}
          </p>
        ) : instance.status === "provisioning" ? (
          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            No result reported by the install workflow yet.
          </p>
        ) : null}

        {incomplete ? (
          <Button
            variant="secondary"
            onClick={() => retry.mutate()}
            disabled={retry.isPending}
          >
            <RotateCw className="mr-2 h-4 w-4" aria-hidden="true" />
            {retry.isPending ? "Retrying…" : "Retry provisioning"}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
